import type { OidcUser } from '../../types/oidc-user-info';
import { getUserId } from '../../utils/idk';
import { useAuthFetch } from '../use-auth-fetch';

export interface PersonalData {
  readonly salutation?: string;
  readonly academicTitle?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly nickname?: string;
  readonly profilePicture?: {
    id: string;
    etag: string;
  };
}

export type UserInfo = {
  personalData: PersonalData;
  personalDataLoading: boolean;
  personalDataError: string | null;
};

const getServiceUrl = (userId: string, folder: string): string => {
  return `v3/customers/${userId}/${folder}`;
};

/**
 * Fetches user information from the user service.
 * @param {OidcUser | null} oidcUser The user info, or null if the user is not logged in.
 * @returns {UserInfo} An object containing the user's personal data, the loading state and an error message.
 */
export function useUserInfo(oidcUser: OidcUser | null): UserInfo {
  const url = getServiceUrl(oidcUser?.sub || getUserId(), 'personalData');

  const {
    data: personalData,
    loading: personalDataLoading,
    error: personalDataError,
  }: {
    data: PersonalData | null;
    loading: boolean;
    error: string | null;
    refetch: () => void;
  } = useAuthFetch(url, {
    method: 'GET',
    resourceHostId: 'customer-profile',
  });

  const pd = personalData || {};

  return {
    personalData: pd,
    personalDataLoading,
    personalDataError,
  };
}
